import React from 'react';
import { Spinner, Brain } from '@phosphor-icons/react';

const OP_LABEL = {
  analyze_all: 'Analyzing all documents',
  reevaluate: 'Re-evaluating document',
  analyze: 'Analyzing document',
};

/* Full-screen blocking overlay shown while a case-wide analysis or a single-document
 * re-evaluation runs. Its fixed inset + high z-index freezes ALL other operations
 * (clicks/tabs/buttons) until the backend reports the run is complete. */
export const AnalysisProgressOverlay = ({ progress }) => {
  if (!progress?.active) return null;

  const total = Number(progress.total) || 0;
  const completed = Number(progress.completed) || 0;
  const pct = total ? Math.min(100, Math.round((completed / total) * 100)) : 5;
  const opLabel = OP_LABEL[progress.operation] || 'Analyzing';

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#09090B]/75 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="analysis-progress-overlay"
      role="alertdialog"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-2xl w-full max-w-md p-7">
        <div className="flex items-center gap-3 mb-1">
          <span className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-[#002FA7]/10">
            <Brain size={22} className="text-[#002FA7]" weight="duotone" />
          </span>
          <div>
            <h3 className="font-outfit text-lg font-bold text-[#09090B] leading-tight" data-testid="analysis-progress-title">{opLabel}</h3>
            <p className="text-xs text-[#6B7280] font-ibm">Deep-diving every page — this may take a few minutes.</p>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-ibm font-semibold text-[#09090B]" data-testid="analysis-progress-count">
              {total > 0 ? `${completed} of ${total} document${total === 1 ? '' : 's'}` : 'Preparing…'}
            </span>
            <span className="text-xs font-ibm text-[#6B7280]">{pct}%</span>
          </div>
          <div className="h-2.5 bg-[#F3F4F6] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#002FA7] rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="analysis-progress-bar"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
          <Spinner size={16} className="animate-spin text-[#002FA7] shrink-0" />
          <div className="min-w-0">
            {progress.current && (
              <p className="text-sm font-ibm font-semibold text-[#09090B] truncate" data-testid="analysis-progress-current">{progress.current}</p>
            )}
            <p className="text-xs font-ibm text-[#6B7280]" data-testid="analysis-progress-stage">{progress.stage || 'Working…'}</p>
          </div>
        </div>

        <p className="mt-4 text-[11px] text-[#9CA3AF] font-ibm text-center">
          Please keep this tab open. All other actions are paused until analysis completes.
        </p>
      </div>
    </div>
  );
};
